/** @jsx jsx */
import { jsx, Box } from 'theme-ui'
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { space } from '~/gatsby-plugin-theme-ui'
import { ColorBoxV2 } from '~/componentsV2/colors/colorBox'
import { ColorsRow, ColorsSizes } from './styled'
import { useMediaQueryV2 } from '~/hooks/use-media-query'
import { TGridLayout } from '~/components/product-item/types'
import { useTooltip } from '~/components/parameters/color-parameter/color-tooltip'

export enum TooltipImageLayout {
  DEFAULT,
  COVER,
}

type Props = {
  variants: ReadonlyArray<{
    title?: string
    image: string
    imageTip: string
    imageSelector: string
    link: string
    onSale: boolean
    compareAtPrice?: {
      amount: number
    }
    currentlyNotInStock?: boolean
  }>
  isRug?: boolean
  visibleVariantsCount?: number
  showButtonMore?: boolean
  tooltipLayout?: TooltipImageLayout
  inStockColorOption?: string
  gridLayout: TGridLayout
}

const ProductItemVariantsListV2: FC<Props> = ({
  variants,
  inStockColorOption,
  gridLayout = 'template-default',
  isRug,
}) => {
  const isSaleUrl = useMemo(
    () => typeof window !== 'undefined' && window.location.href.indexOf('sale') >= 0,
    []
  )
  const currentVariants = useMemo(() => {
    const salesVariants =
      variants.filter(variant => variant.compareAtPrice?.amount || variant.onSale) || null

    return isSaleUrl ? salesVariants : variants
  }, [variants])

  const colorsRowRef = React.createRef<HTMLDivElement>()
  const [variantsToShow, setVariantsToShow] = useState<readonly any[] | null>(null)

  const isMobile = useMediaQueryV2({
    query: {
      maxWidth: 767,
    },
  })
  const isTablet = useMediaQueryV2({
    query: {
      maxWidth: 1024,
      minWidth: 768,
    },
  })

  const getColorSize = (measure: 'margin' | 'size'): number => {
    if (isMobile) {
      return ColorsSizes[gridLayout][measure][0] as number
    } else if (isTablet) {
      return ColorsSizes[gridLayout][measure][2] as number
    }
    return ColorsSizes[gridLayout][measure][3] as number
  }

  useEffect(() => {
    if (colorsRowRef.current && !variantsToShow) {
      const colorsRowWidth = colorsRowRef.current.offsetWidth
      const size = getColorSize('margin') + getColorSize('size')
      const colorsRowCapacity = Math.round(colorsRowWidth / size) - 2

      const targets = isTablet ? 2 : 1

      const maxVariantsToShow = Math.max(isMobile ? 3 - targets : 5 - targets, 0)
      const toShow = colorsRowCapacity < maxVariantsToShow ? maxVariantsToShow : colorsRowCapacity

      if (currentVariants?.length > toShow) {
        setVariantsToShow(currentVariants.slice(0, toShow))
      } else {
        setVariantsToShow(currentVariants)
      }

      return
    }
  }, [colorsRowRef.current])

  const { Tooltip, createdContent, tooltipId } = useTooltip({ id: 'product-color-swatch' })

  return (
    <ColorsRow ref={colorsRowRef} gridLayout={gridLayout} className={`color-box-${gridLayout}`}>
      {variantsToShow?.length ? (
        <Fragment>
          {variantsToShow.map((variant, index) => {
            return (
              <Box
                as="li"
                key={variant.image}
                sx={{
                  width: ColorsSizes[gridLayout].size,
                  height: ColorsSizes[gridLayout].size,
                  display: ['block'],
                  position: 'relative',
                }}
              >
                <Link
                  className="color-box-link"
                  to={variant.link}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={createdContent(
                    variant.title,
                    isRug ? variant.image : variant.imageTip ?? variant.image
                  )}
                  data-tooltip-delay-show={500}
                  data-tooltip-delay-hide={500}
                  state={{
                    comesFromSaleCollections: isSaleUrl,
                  }}
                >
                  <ColorBoxV2
                    value={variant.title || ''}
                    src={variant.imageSelector || variant.image}
                    selectedValue={index === 0 ? variant.title : inStockColorOption}
                    currentlyNotInStock={variant.currentlyNotInStock}
                    showTooltip={false}
                  />
                  {index === variantsToShow.length - 1 &&
                  currentVariants.length > variantsToShow.length ? (
                    <Box
                      sx={{
                        color: 'muted',
                        fontSize: [2, 4],
                        pl: space.sm,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        position: 'absolute',
                        right: [`-90%`, '-90%'],
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      + {currentVariants.length - variantsToShow.length}
                    </Box>
                  ) : null}
                </Link>
              </Box>
            )
          })}
        </Fragment>
      ) : null}
      <Tooltip />
    </ColorsRow>
  )
}

export default ProductItemVariantsListV2
